import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import './GlobalErrorModal.css';

const STUDENT_SAVE_SCOPE = 'student-save';
const STUDENT_SAVE_ARM_MS = 60000;

const normalizeError = (detail = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  title: String(detail.title || 'Não foi possível salvar o aluno'),
  message: String(detail.message || 'Verifique os dados informados e tente novamente.'),
  focusTarget: detail.focusTarget || null,
});

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const isStudentSaveButton = (target) => {
  if (!(target instanceof Element)) return false;
  const button = target.closest('button');
  if (!button || button.disabled) return false;

  const card = button.closest('.students__card');
  const heading = card?.querySelector('.students__section-title h2');
  const cardTitle = normalizeText(heading?.textContent);
  const buttonLabel = normalizeText(button.textContent);

  return cardTitle.includes('Cadastro de Alunos') && buttonLabel === 'Salvar';
};

const currentStudentSaveError = () => {
  if (typeof document === 'undefined') return null;
  const element = document.querySelector('.students > .students__alert.students__alert--error');
  if (!element) return null;
  const message = normalizeText(element.textContent);
  return message ? { element, message } : null;
};

export const showGlobalError = (detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('bpm:error', { detail }));
};

export default function GlobalErrorModal() {
  const [queue, setQueue] = useState([]);
  const current = queue[0] || null;

  useEffect(() => {
    const onError = (event) => {
      if (event?.detail?.scope !== STUDENT_SAVE_SCOPE) return;
      const next = normalizeError(event?.detail || {});
      setQueue((previous) => {
        const last = previous[previous.length - 1];
        if (last && last.title === next.title && last.message === next.message) return previous;
        return [...previous, next].slice(-6);
      });
    };
    window.addEventListener('bpm:error', onError);
    return () => window.removeEventListener('bpm:error', onError);
  }, []);

  useEffect(() => {
    let armedUntil = 0;
    let lastMessage = '';
    let postClickTimer = null;

    const disarm = () => {
      armedUntil = 0;
      lastMessage = '';
    };

    const surfaceStudentSaveError = () => {
      if (!armedUntil || Date.now() > armedUntil) {
        disarm();
        return;
      }

      const error = currentStudentSaveError();
      if (!error || error.message === lastMessage) return;
      lastMessage = error.message;

      showGlobalError({
        scope: STUDENT_SAVE_SCOPE,
        title: 'Não foi possível salvar o aluno',
        message: error.message,
      });
      disarm();
    };

    const onClick = (event) => {
      if (!isStudentSaveButton(event.target)) return;

      armedUntil = Date.now() + STUDENT_SAVE_ARM_MS;
      lastMessage = '';
      if (postClickTimer) window.clearTimeout(postClickTimer);

      // Também cobre a repetição do mesmo erro. Quando o salvamento é válido,
      // StudentManagement limpa o erro antes deste pequeno atraso e nada é exibido.
      postClickTimer = window.setTimeout(surfaceStudentSaveError, 150);
    };

    const observer = new MutationObserver(() => surfaceStudentSaveError());
    document.addEventListener('click', onClick, true);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      document.removeEventListener('click', onClick, true);
      observer.disconnect();
      if (postClickTimer) window.clearTimeout(postClickTimer);
    };
  }, []);

  useEffect(() => {
    if (!current) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        const focusTarget = current.focusTarget;
        setQueue((previous) => previous.slice(1));
        window.setTimeout(() => focusTarget?.focus?.(), 0);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [current]);

  if (!current || typeof document === 'undefined') return null;

  const close = () => {
    const focusTarget = current.focusTarget;
    setQueue((previous) => previous.slice(1));
    window.setTimeout(() => focusTarget?.focus?.(), 0);
  };

  return createPortal(
    <div className="bpm-error-modal__backdrop" role="presentation">
      <section
        className="bpm-error-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="bpm-error-modal-title"
        aria-describedby="bpm-error-modal-message"
      >
        <div className="bpm-error-modal__icon" aria-hidden="true">
          <FaExclamationTriangle />
        </div>
        <div className="bpm-error-modal__content">
          <div className="bpm-error-modal__eyebrow">Atenção</div>
          <h2 id="bpm-error-modal-title">{current.title}</h2>
          <p id="bpm-error-modal-message">{current.message}</p>
          {queue.length > 1 ? (
            <div className="bpm-error-modal__pending">Há mais {queue.length - 1} aviso(s) aguardando.</div>
          ) : null}
          <div className="bpm-error-modal__actions">
            <button type="button" autoFocus onClick={close}>Entendi</button>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}
